import { useEffect } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import useAuthStore from '../store/authStore.js'
import { auth } from '../api/endpoints.js'

/**
 * A persisted token outlives the server's JWT lifetime, so a returning tab used
 * to render the whole app and then 401 on every request.  Verify the token once
 * per load against /auth/me: refresh the cached staff record on success, drop
 * the session on a 401.
 */
export default function ProtectedRoute({ children }) {
  const { token, isAuthenticated, setAuth, logout } = useAuthStore()
  const location = useLocation()

  const { data, error, isLoading, isSuccess } = useQuery({
    queryKey: ['auth', 'me', token],
    queryFn: () => auth.getMe().then((r) => r.data),
    enabled: !!token && isAuthenticated,
    staleTime: 5 * 60_000,
    retry: false,
  })

  const status = error?.response?.status
  const expired = status === 401 || status === 403

  useEffect(() => {
    if (expired) logout()
  }, [expired, logout])

  useEffect(() => {
    if (isSuccess && data) setAuth(token, data)
  }, [isSuccess, data, token, setAuth])

  if (!isAuthenticated || expired) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // First load with a stored token — hold the shell until the token is proven.
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    )
  }

  return children
}
